/**
 * Swamithra — the user record, registration and the profile.
 *
 * Registration and login are the SAME screen: a number is typed, the server says
 * whether it is known, and the profile fields are revealed only when it is not
 * (US-00-001.5/.6). The schemas below are shaped around that flow rather than
 * around two separate forms.
 */

import { z } from "zod"

import { OTP_LENGTH } from "./constants"
import { userTypeSchema } from "./enums"
import { e164Schema, mobileNumberSchema } from "./phone"

/**
 * Deliberately permissive, deliberately not `z.string().email()`.
 *
 * The email is optional, stored unverified, and nothing is ever sent to it
 * (US-00-001.13). A strict RFC 5322 matcher rejects addresses that work, and the
 * only cost of accepting one that does not is a field nobody uses.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const emailSchema = z
	.string()
	.trim()
	.max(254, "That email address is too long.")
	.refine(value => EMAIL_RE.test(value), "Enter a valid email address.")

/**
 * The optional-email field as it appears on a form: blank is allowed and becomes
 * null, anything else must look like an address.
 */
export const optionalEmailSchema = z
	.string()
	.trim()
	.max(254, "That email address is too long.")
	.refine(value => value === "" || EMAIL_RE.test(value), "Enter a valid email address.")
	.transform(value => (value === "" ? null : value))

export const fullNameSchema = z
	.string()
	.trim()
	.min(1, "Enter your full name.")
	.max(120, "That name is too long.")

// -----------------------------------------------------------------------------
// The record
// -----------------------------------------------------------------------------

export const userSchema = z.object({
	id: z.string(),
	fullName: z.string(),
	/** Always E.164 — normalised before storage and before lookup. */
	mobileNumber: e164Schema,
	email: z.string().nullable(),
	userType: userTypeSchema,
	createdAt: z.string(),
})

export type User = z.infer<typeof userSchema>

// -----------------------------------------------------------------------------
// The entry screen
// -----------------------------------------------------------------------------

/**
 * Sent when the number is unrecognised and the profile fields have been
 * revealed.
 *
 * Full Name and User Type are required BEFORE an OTP can be requested
 * (US-00-001.6); the account itself is not created until the code is verified.
 */
export const registrationDetailsSchema = z.object({
	fullName: fullNameSchema,
	userType: userTypeSchema,
	email: optionalEmailSchema.optional(),
})

/**
 * The OUTPUT shape — email already narrowed to `string | null`.
 *
 * A form holds the INPUT shape (`RegistrationDetailsInput`), where the email is
 * still whatever was typed. The distinction matters because these schemas
 * transform: a caller that passes raw form values must go through `parse`, which
 * is where the mobile number is normalised to E.164 (US-00-001.12).
 */
export type RegistrationDetails = z.infer<typeof registrationDetailsSchema>
export type RegistrationDetailsInput = z.input<typeof registrationDetailsSchema>

/**
 * What the entry screen submits to ask for a code.
 *
 * The registration details are nested HERE, in the client's own shape, and
 * flattened onto the request body by ../api/auth — the service reads
 * `full_name`, `email` and `user_type` at the top level and its decoder refuses
 * a field it does not know, so a nested `registration` object is a 400 and no
 * code at all.
 */
export const otpRequestSchema = z.object({
	mobileNumber: mobileNumberSchema,
	/** Omitted for a number the server already knows. */
	registration: registrationDetailsSchema.optional(),
})

export type OtpRequest = z.infer<typeof otpRequestSchema>
export type OtpRequestInput = z.input<typeof otpRequestSchema>

/**
 * What the server says after a code has been dispatched.
 *
 * `resendAvailableAt` is the authority for the resend cooldown — a client-side
 * timer alone would reset on a reload and let a user hammer the endpoint
 * (US-00-001.10).
 */
/**
 * THE SERVICE CALLS IT `registered`. This package calls it `isRegistered`,
 * because a boolean read off a form's state reads better with the prefix, and
 * because both the lookup and the OTP response carry the same fact. The mapping
 * happens once, here.
 */
function withRegistered(value: unknown): unknown {
	if (value === null || typeof value !== "object" || Array.isArray(value)) return value
	const record = value as Record<string, unknown>
	if ("isRegistered" in record) return record
	return { ...record, isRegistered: record.registered }
}

export const otpChallengeSchema = z.preprocess(
	withRegistered,
	z.object({
		/** Drives whether the profile fields stay hidden (US-00-001.5). */
		isRegistered: z.boolean(),
		/** Echoed in E.164, so the verify step sends back what was looked up. */
		mobileNumber: z.string().optional(),
		expiresAt: z.string(),
		/** The authority for the resend cooldown (US-00-001.10). */
		resendAvailableAt: z.string(),
		/**
		 * OPTIONAL: the per-number hourly cap is enforced server-side and
		 * refused with `resend_limit`, and the service does not report a
		 * remaining count. A required field here failed the parse of every
		 * successful OTP request.
		 */
		resendsRemaining: z.number().int().nonnegative().optional(),
	}),
)

export type OtpChallenge = z.output<typeof otpChallengeSchema>

/** Whether a number is known, asked before any code is dispatched. */
export const numberLookupSchema = z.preprocess(
	withRegistered,
	z.object({
		isRegistered: z.boolean(),
		/** The normalised form the server resolved it to (US-00-001.12). */
		mobileNumber: z.string().optional(),
	}),
)

export type NumberLookup = z.output<typeof numberLookupSchema>

export const otpCodeSchema = z
	.string()
	.trim()
	.regex(new RegExp(`^\\d{${OTP_LENGTH}}$`), `Enter the ${OTP_LENGTH}-digit OTP.`)

/**
 * The submitted code, and NOTHING ELSE.
 *
 * The account is still created only on successful verification (US-00-001.6) —
 * but the profile details travel with the OTP REQUEST and are held against the
 * challenge until then. Repeating them here is a field the service's decoder
 * does not know, which is a 400 on the one call the whole flow depends on.
 */
export const otpVerifySchema = z.object({
	mobileNumber: mobileNumberSchema,
	code: otpCodeSchema,
})

export type OtpVerify = z.infer<typeof otpVerifySchema>
export type OtpVerifyInput = z.input<typeof otpVerifySchema>

/**
 * Who a verified session belongs to.
 *
 * The SAME shape for a taxpayer and for an Expert — one sign-in endpoint per
 * audience, one response body — with `kind` saying which and `role` empty for a
 * user. It is deliberately thin: the profile is `GET /me`, and a login response
 * that carried a whole user record would be a second copy of it to keep in step.
 */
export const identitySchema = z.object({
	id: z.string(),
	kind: z.enum(["user", "expert"]),
	fullName: z.string(),
	/** `expert` or `super_admin` on the panel; empty string for a taxpayer. */
	role: z.string().default(""),
})

export type Identity = z.infer<typeof identitySchema>

/**
 * A verified session, as OTP verification answers.
 *
 * `accessToken` is present for native clients, which must hold the JWT in
 * SecureStore. On web it is IGNORED and never persisted: the browser's copy is
 * the httpOnly, Secure, SameSite=Lax cookie the server set on the same response,
 * and nothing token-shaped may exist in localStorage or sessionStorage
 * (US-00-001.15). See ../session for where that decision is made once.
 *
 * THE SESSION AND THE PROFILE ARE TWO DIFFERENT RESPONSES, and this package used
 * to pretend they were one. Verification answers `{token, expires_at,
 * is_new_user, identity}`; `GET /me` — the session probe, the service having no
 * `/auth/session` route — answers the user record. Lifting the second into the
 * first meant the FIRST no longer parsed, and a wrong code and a correct one
 * failed identically.
 *
 * The wire spells the token `token` and the flag `is_new_user`; both are mapped
 * once, below.
 */
export const sessionSchema = z.preprocess(
	value => {
		if (value === null || typeof value !== "object") return value
		const record = value as Record<string, unknown>
		return {
			...record,
			accessToken: record.accessToken ?? record.token,
			isNewAccount: record.isNewAccount ?? record.isNewUser,
		}
	},
	z.object({
		identity: identitySchema,
		accessToken: z.string().optional(),
		/** RFC 3339 — the idle deadline, which slides on every authenticated call. */
		expiresAt: z.string().optional(),
		/** True on the response that created the account, for the analytics event. */
		isNewAccount: z.boolean().optional(),
	}),
)

export type Session = z.output<typeof sessionSchema>

// -----------------------------------------------------------------------------
// Profile
// -----------------------------------------------------------------------------

/**
 * The ONLY two fields a user may change (US-00-001.4).
 *
 * `.strict()` is the point of this schema: a PATCH carrying `mobileNumber` or
 * `userType` fails here, at the call site, with a stack trace that names the
 * bug — and the server rejects the same body with 400 if a client is built that
 * bypasses this. The mobile number is read-only and the user type has no edit
 * control at all.
 */
export const profileUpdateSchema = z
	.object({
		fullName: fullNameSchema.optional(),
		email: optionalEmailSchema.optional(),
	})
	.strict()
	.refine(
		value => value.fullName !== undefined || value.email !== undefined,
		"Change something before saving.",
	)

export type ProfileUpdate = z.infer<typeof profileUpdateSchema>
export type ProfileUpdateInput = z.input<typeof profileUpdateSchema>
